import { createModel, RematchDispatch } from '@rematch/core';
import { message } from 'antd';
import axios from 'axios';
import produce from 'immer';
import {
  DEFAULT_PAGINATION,
  FILE_NAME_TYPE,
  INFO_ITEM_TYPE,
} from '../../commons/constants/constants';
import { eStoreHost } from '../../services/eStoreAxios';
import { uploadFiles } from '../../services/uploader.service';
import { PageRequest } from '../../types/CommonTypes';
import { BrandInfoData, LocationInfoData, ProductInfoData } from '../../types/ItemInfoData';
import { storeName as entityStore } from '../entities/itemInfo.entity';

import { RootModel } from '../models';

export const storeName = 'itemInfos';

const initialState = {
  ids: [],
  pagination: DEFAULT_PAGINATION,
  criteria: {},
  loading: false,
  visible: false,
  editId: null,

  data: [],
  edit: {
    visible: false,
    item: null,
  },
};

export const itemInfos: any = createModel<RootModel>()({
  name: storeName,
  state: initialState,
  reducers: {
    setIds: produce((state, payload) => {
      state.ids = payload;
    }),
    setPagination: produce((state, payload) => {
      state.pagination = payload;
    }),
    setCriteria: produce((state, payload) => {
      state.criteria = payload;
    }),
    setLoading: produce((state, payload) => {
      state.loading = payload;
    }),
    setVisible: produce((state, payload) => {
      state.visible = payload;
    }),
    setEditId: produce((state, payload) => {
      state.editId = payload;
    }),
    setItem: produce((state, payload) => {
      state.edit = {
        ...state.edit,
        visible: !!payload,
        item: payload,
      };
    }),
  },
  effects: (dispatch: RematchDispatch<typeof itemInfos>) => ({
    async reloadList(payload, state) {
      const { criteria, pagination } = state[storeName];
      await this.filterAsync({ ...pagination, ...criteria });
    },
    async resetFilter(payload, state) {
      const { pagination } = state[storeName];
      await this.filterAsync({ ...pagination, page: 0 });
    },
    async pagingAsync(payload: PageRequest, state) {
      const { criteria = {}, pagination = {} } = state[storeName];
      await this.filterAsync({ ...pagination, ...criteria, ...payload });
    },
    async filterAsync(params = {}, state) {
      this.setLoading(true);
      try {
        const data = await dispatch[entityStore].load(params);
        if (data && data.content) {
          this.setIds(data.content.map((itemInfo) => itemInfo.id));
          this.setPagination((({ content, ...otherProps }) => otherProps)(data));
          this.setCriteria(params);
        }
      } catch (e) {
        console.error(e);
        message.error('Failed to search item info');
      } finally {
        this.setLoading(false);
      }
    },
    async getEditItemInfo(id) {
      this.setLoading(true);
      try {
        const itemInfo = await dispatch[entityStore].loadById(id);
        this.setEditId(itemInfo.id);
      } catch (e) {
        console.error(e);
        message.error('Fail to get item info');
      } finally {
        this.setLoading(false);
      }
    },
    async saveItemInfo(payload, state) {
      if (!payload) {
        return null;
      }

      this.setLoading(true);
      try {
        const { pagination } = state[storeName];
        const { user } = state.user;
        const editing = !!payload.id;
        const uploadActions = [];
        const uploadCollectionActions = [];
        const data = { ...payload };
        if (data) {
          const { type, icon, id } = data;
          if (icon && (!icon.url || !icon.url.includes(eStoreHost))) {
            const promiseFile = uploadFiles(
              id,
              [icon],
              FILE_NAME_TYPE.ITEM_INFO.ICON,
              user.id,
              'info/icon'
            ).then((result) => {
              const { filesList } = result;
              if (filesList.length) {
                const firstFile = filesList[0];
                data.icon = { id: firstFile.id, url: firstFile.url };
              }
            });
            uploadActions.push(promiseFile);
          }
          switch (type) {
            case INFO_ITEM_TYPE.LOCATION:
              const { audioFile } = data as LocationInfoData;
              if (audioFile) {
                const promiseFile = uploadFiles(
                  id,
                  [audioFile],
                  FILE_NAME_TYPE.ITEM_INFO.AUDIO,
                  user.id,
                  'info/audio'
                ).then((result) => {
                  const { filesList } = result;
                  if (filesList.length) {
                    const firstFile = filesList[0];
                    (data as LocationInfoData).audioFile = { id: firstFile.id, url: firstFile.url };
                  }
                });
                uploadActions.push(promiseFile);
              }
              break;
            case INFO_ITEM_TYPE.BRAND:
              const { logo } = data as BrandInfoData;
              if (logo) {
                const promiseFile = uploadFiles(
                  id,
                  [logo],
                  FILE_NAME_TYPE.ITEM_INFO.LOGO,
                  user.id,
                  'info/audio'
                ).then((result) => {
                  const { filesList } = result;
                  if (filesList.length) {
                    const firstFile = filesList[0];
                    (data as BrandInfoData).logo = { id: firstFile.id, url: firstFile.url };
                  }
                });
                uploadActions.push(promiseFile);
              }
              break;
            case INFO_ITEM_TYPE.PRODUCT:
              const { collections } = data as ProductInfoData;
              if (collections && collections.length) {
                collections.forEach((collection) => {
                  if (!collection) {
                    return;
                  }
                  const { model3DAndroid, model3DIos } = collection;
                  if (model3DAndroid) {
                    const promiseFile = uploadFiles(
                      collection.id,
                      [model3DAndroid],
                      FILE_NAME_TYPE.ITEM_INFO.MODEL_3D_ANDROID,
                      user.id,
                      'info/model3d'
                    ).then((result) => {
                      const { filesList } = result;
                      if (filesList.length) {
                        const firstFile = filesList[0];
                        collection.model3DAndroid = { id: firstFile.id, url: firstFile.url };
                      }
                    });
                    uploadCollectionActions.push(promiseFile);
                  }

                  if (model3DIos) {
                    const promiseFile = uploadFiles(
                      collection.id,
                      [model3DIos],
                      FILE_NAME_TYPE.ITEM_INFO.MODEL_3D_IOS,
                      user.id,
                      'info/model3d'
                    ).then((result) => {
                      const { filesList } = result;
                      if (filesList.length) {
                        const firstFile = filesList[0];
                        collection.model3DIos = { id: firstFile.id, url: firstFile.url };
                      }
                    });
                    uploadCollectionActions.push(promiseFile);
                  }
                });
              }
              break;
            case INFO_ITEM_TYPE.ECOMMERCE_PRODUCT:
              break;
            default:
              console.log('unknow type: ', type);
          }
        }

        await axios.all(uploadActions);
        await axios.all(uploadCollectionActions);
        let result;
        if (editing) {
          result = await dispatch[entityStore].update(data);
        } else {
          result = await dispatch[entityStore].create(data);
        }
        message.success('Success to save item');
        this.setCriteria({});
        this.filterAsync({
          ...pagination,
          page: 0,
        });
        return result;
      } catch (e) {
        message.error('Fail to save item');
        console.error('Fail to save item: ', e);
      } finally {
        this.setLoading(false);
      }
      return null;
    },
    async deleteItemInfo(payload, state) {
      if (!payload) {
        return null;
      }

      try {
        this.setLoading(true);
        await dispatch[entityStore].delete(payload.id);
        message.success(`Success to delete item info ${payload.code}`);
        this.reloadList();
      } catch (e) {
        message.error(`Fail to delete item info ${payload.code}`);
        console.error(`Fail to delete item info ${payload.code}: `, e);
      } finally {
        this.setLoading(false);
      }
    },
  }),
});

export default itemInfos;
